import classNames from "classnames";
import {
  FieldMessageProps,
  FieldValueProps,
} from "./FieldValue.types";

/** The id a field's help text is given, so a control can point at it. */
export const helpTextId = (name: string) => `${name}-help`;

/** The id a field's error message is given, for the same reason. */
export const errorTextId = (name: string) => `${name}-error`;

/**
 * What a control's `aria-describedby` should be: its help text, its error, both or
 * neither. Centralised so every field type associates them the same way.
 */
export function fieldDescribedBy(
  name: string,
  helpText?: string,
  error?: string
) {
  const ids = [
    helpText && helpTextId(name),
    error && errorTextId(name),
  ].filter(Boolean);

  return ids.length > 0 ? ids.join(" ") : undefined;
}

/** A control's classes, with Bootstrap's invalid state when there is an error. */
export function fieldControlClass(base: string, error?: string) {
  return classNames(base, { "is-invalid": error });
}

/**
 * A field's error message, in space reserved whether or not there is one, so a
 * single-line message appears without moving everything below it. The slot replaces the
 * field's bottom margin rather than adding to it; see `.field-value__message`.
 */
export function FieldMessage({ name, error }: FieldMessageProps) {
  return (
    <div className="field-value__message">
      {error && (
        <p
          id={errorTextId(name)}
          className="invalid-feedback d-block mb-0"
        >
          {error}
        </p>
      )}
    </div>
  );
}

export function FieldValue({
  label,
  htmlFor,
  children,
  required = false,
  helpText,
  error,
  className = "",
  labelActions,
}: FieldValueProps) {
  const labelElement = (
    <label
      // Bold rather than semibold: the marker for a required field is the weight
      // alone - there is no asterisk - so it has to be far enough from the plain
      // label beside it to be read as a difference rather than as the same word.
      className={classNames("field-value__label form-label", {
        "fw-bold": required,
        // The row below carries the spacing instead.
        "mb-0": labelActions,
      })}
      htmlFor={htmlFor}
    >
      {label}
    </label>
  );

  return (
    <div className={classNames("field-value", className)}>
      {labelActions ? (
        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
          {labelElement}
          {labelActions}
        </div>
      ) : (
        labelElement
      )}
      {children}
      {helpText && (
        <p
          id={helpTextId(htmlFor)}
          className="form-text small text-body-secondary mb-0"
        >
          {helpText}
        </p>
      )}
      <FieldMessage name={htmlFor} error={error} />
    </div>
  );
}

export default FieldValue;
